import { useCallback, useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Hub } from "aws-amplify/utils";
import { AppShell } from "./components/AppShell";
import { AdminShell } from "./components/AdminShell";
import { loadCurrentUser, type AuthedUser } from "./lib/auth";
import { fetchMe, MeContext, type Me } from "./lib/me";
import SignIn from "./routes/SignIn";
import SignUp from "./routes/SignUp";
import VerifyEmail from "./routes/VerifyEmail";
import Dashboard from "./routes/Dashboard";
import Placeholder from "./routes/Placeholder";
import ClaimSlug from "./routes/ClaimSlug";
import SetupWizard from "./routes/SetupWizard";
import Cases from "./routes/Cases";
import CaseDetail from "./routes/CaseDetail";
import Categories from "./routes/Categories";
import CategoryDetail from "./routes/CategoryDetail";
import NewCategory from "./routes/NewCategory";
import Settings from "./routes/Settings";
import Review from "./routes/Review";
import Audit from "./routes/Audit";
import Connectors from "./routes/Connectors";
import ConnectorEditor from "./routes/ConnectorEditor";
import Services from "./routes/Services";
import Landing from "./routes/Landing";
import Help from "./routes/Help";
import AdminOverview from "./routes/admin/Overview";
import AdminTenants from "./routes/admin/Tenants";
import AdminTenantDetail from "./routes/admin/TenantDetail";
import AdminPaidPlanRequests from "./routes/admin/PaidPlanRequests";
import AdminUsage from "./routes/admin/Usage";

type AuthState =
  | { status: "loading" }
  | { status: "signed_out" }
  | { status: "signed_in"; user: AuthedUser; me: Me | null; meLoading: boolean };

export default function App() {
  const [auth, setAuth] = useState<AuthState>({ status: "loading" });

  const refreshMe = useCallback(async () => {
    try {
      const me = await fetchMe();
      setAuth((prev) =>
        prev.status === "signed_in" ? { ...prev, me, meLoading: false } : prev,
      );
    } catch {
      setAuth((prev) =>
        prev.status === "signed_in" ? { ...prev, me: null, meLoading: false } : prev,
      );
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const refreshAuth = async () => {
      const user = await loadCurrentUser();
      if (cancelled) return;
      if (!user) {
        setAuth({ status: "signed_out" });
        return;
      }
      setAuth({ status: "signed_in", user, me: null, meLoading: true });
      try {
        const me = await fetchMe();
        if (!cancelled) {
          setAuth({ status: "signed_in", user, me, meLoading: false });
        }
      } catch {
        if (!cancelled) {
          setAuth({ status: "signed_in", user, me: null, meLoading: false });
        }
      }
    };
    refreshAuth();
    const unsub = Hub.listen("auth", () => refreshAuth());
    return () => {
      cancelled = true;
      unsub();
    };
  }, []);

  if (auth.status === "loading") return <FullScreenLoader />;

  if (auth.status === "signed_out") {
    return (
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/signin" element={<SignIn />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/verify" element={<VerifyEmail />} />
        <Route path="*" element={<RedirectHome />} />
      </Routes>
    );
  }

  if (auth.meLoading || !auth.me) return <FullScreenLoader />;

  const { me } = auth;

  return (
    <MeContext.Provider value={{ me, refresh: refreshMe }}>
      {me.isSuperAdmin ? (
        // Super admins live entirely in /admin/*. Everything else redirects.
        <AdminShell email={me.user.email}>
          <Routes>
            <Route path="/admin" element={<AdminOverview />} />
            <Route path="/admin/tenants" element={<AdminTenants />} />
            <Route path="/admin/tenants/:tenantId" element={<AdminTenantDetail />} />
            <Route path="/admin/paid-plan-requests" element={<AdminPaidPlanRequests />} />
            <Route path="/admin/usage" element={<AdminUsage />} />
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Routes>
        </AdminShell>
      ) : !me.hasTenant ? (
        <Routes>
          <Route path="/claim-slug" element={<ClaimSlug />} />
          <Route path="*" element={<Navigate to="/claim-slug" replace />} />
        </Routes>
      ) : !me.setupComplete ? (
        <Routes>
          <Route path="/setup" element={<SetupWizard />} />
          <Route path="*" element={<Navigate to="/setup" replace />} />
        </Routes>
      ) : (
        <AppShell user={auth.user}>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/review" element={<Review />} />
            <Route path="/cases" element={<Cases />} />
            <Route path="/cases/:caseId" element={<CaseDetail />} />
            <Route path="/categories" element={<Categories />} />
            <Route path="/categories/new" element={<NewCategory />} />
            <Route path="/categories/:intentId" element={<CategoryDetail />} />
            <Route path="/connectors" element={<Connectors />} />
            <Route path="/connectors/new" element={<ConnectorEditor />} />
            <Route path="/connectors/:id" element={<ConnectorEditor />} />
            <Route path="/services" element={<Services />} />
            <Route path="/audit" element={<Audit />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/help" element={<Help />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AppShell>
      )}
    </MeContext.Provider>
  );
}

function FullScreenLoader() {
  return (
    <div className="grid min-h-full place-items-center text-sm text-gray-500">Loading…</div>
  );
}

function RedirectHome() {
  const location = useLocation();
  if (
    location.pathname.startsWith("/signin") ||
    location.pathname.startsWith("/signup") ||
    location.pathname.startsWith("/verify")
  ) {
    return null;
  }
  return <Navigate to="/" replace />;
}
